/**
 * Map-Reduce topology scene — Distribution Campus.
 *
 * Campus layout:
 *   - Coordinator building (5x5 manager) on the left
 *   - Worker buildings (4x4 factory) stacked vertically in center
 *   - Reducer building (5x5 manager) on the right
 *   - Stone paths: coordinator → each worker → reducer
 *   - Conveyor furniture along paths
 *
 * Scales: more workers = taller center column.
 */

import type { SceneLayout, TopologyInfo, BuildingFootprint, CampusDef } from "./SceneLayout.js";
import { campusToLayout, routePath } from "./SceneLayout.js";
import { getFurnitureForBuilding } from "./FurniturePresets.js";

const MGR_SIZE = 6;
const WORKER_SIZE = 4;
const GAP = 4;

/** Detect map-reduce pattern: one source fanning out, one sink fanning in. */
export function isMapReducePattern(topo: TopologyInfo): boolean {
  const outCount = new Map<string, number>();
  const inCount = new Map<string, number>();
  for (const e of topo.edges) {
    outCount.set(e.from, (outCount.get(e.from) ?? 0) + 1);
    inCount.set(e.to, (inCount.get(e.to) ?? 0) + 1);
  }
  const hasFanOut = [...outCount.values()].some((c) => c > 1);
  const hasFanIn = [...inCount.values()].some((c) => c > 1);
  return hasFanOut && hasFanIn && topo.agents.length >= 3;
}

export function mapReduceLayout(topo: TopologyInfo): SceneLayout {
  const buildings: BuildingFootprint[] = [];

  // Find coordinator and reducer
  const outCount = new Map<string, number>();
  const inCount = new Map<string, number>();
  for (const e of topo.edges) {
    outCount.set(e.from, (outCount.get(e.from) ?? 0) + 1);
    inCount.set(e.to, (inCount.get(e.to) ?? 0) + 1);
  }

  const coordinatorId = topo.entryPoint ?? topo.agents[0]?.id ?? "";
  let reducerId = "";
  let maxIn = 0;
  for (const [id, count] of inCount) {
    if (count > maxIn && id !== coordinatorId) {
      maxIn = count;
      reducerId = id;
    }
  }
  if (!reducerId) {
    reducerId = topo.agents[topo.agents.length - 1]?.id ?? coordinatorId;
  }

  const workers = topo.agents.filter((a) => a.id !== coordinatorId && a.id !== reducerId);
  const workerCount = workers.length;

  // Worker column (center)
  const workerColX = MGR_SIZE + GAP;
  const workerTotalH = workerCount * WORKER_SIZE + Math.max(0, workerCount - 1) * GAP;

  // Coordinator (left), vertically centered
  const centerRow = Math.max(0, Math.floor((workerTotalH - MGR_SIZE) / 2));
  const coordBuilding: BuildingFootprint = {
    id: "coordinator",
    col: 0,
    row: centerRow,
    width: MGR_SIZE,
    height: MGR_SIZE,
    floorType: "manager",
    agents: [{
      agentId: coordinatorId,
      localCol: 3,
      localRow: 3,
      deskType: "large",
      isEntryPoint: true,
    }],
    furniture: getFurnitureForBuilding("entry", "datacenter", 0, centerRow, MGR_SIZE, MGR_SIZE),
  };
  buildings.push(coordBuilding);

  // Worker buildings (center column, stacked)
  const workerBuildings: BuildingFootprint[] = [];
  for (let i = 0; i < workerCount; i++) {
    const bRow = i * (WORKER_SIZE + GAP);
    const b: BuildingFootprint = {
      id: `worker-${i}`,
      col: workerColX,
      row: bRow,
      width: WORKER_SIZE,
      height: WORKER_SIZE,
      floorType: "factory",
      agents: [{
        agentId: workers[i].id,
        localCol: 2,
        localRow: 2,
        deskType: "small",
        isEntryPoint: false,
      }],
      furniture: getFurnitureForBuilding("worker", "datacenter", workerColX, bRow, WORKER_SIZE, WORKER_SIZE),
    };
    buildings.push(b);
    workerBuildings.push(b);
  }

  // Reducer (right), vertically centered
  const reducerColX = workerColX + WORKER_SIZE + GAP;
  const reducerBuilding: BuildingFootprint = {
    id: "reducer",
    col: reducerColX,
    row: centerRow,
    width: MGR_SIZE,
    height: MGR_SIZE,
    floorType: "manager",
    agents: [{
      agentId: reducerId,
      localCol: 3,
      localRow: 3,
      deskType: "large",
      isEntryPoint: false,
    }],
    furniture: getFurnitureForBuilding("exit", "datacenter", reducerColX, centerRow, MGR_SIZE, MGR_SIZE),
  };
  buildings.push(reducerBuilding);

  // Route paths: coordinator → each worker → reducer
  const pathCells: Array<{ col: number; row: number }> = [];
  for (const wb of workerBuildings) {
    pathCells.push(...routePath(coordBuilding, wb));
    pathCells.push(...routePath(wb, reducerBuilding));
  }

  const campus: CampusDef = {
    buildings,
    pathCells,
    decorations: [],
  };

  return campusToLayout(campus);
}
